import { CatchError } from 'Exceptions/exceptions'
import ApiTrafficLogsService from 'Main/service/ApiTrafficLogsService'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getRefreshToken } from 'variables/common'
import useCopyToClipboard from 'views/components/Hook/CopyToClipboard'
import { ApiTrafficLogSummary } from './Summary/ApiTrafficLogSummary'
import JsonViewerPanel from 'views/components/JsonViewer/JsonViewerPanel'


function ApiTrafficLogsDetail () {
  const { id } = useParams()
  const navigate = useNavigate()
  const refreshToken = getRefreshToken()
  const apiTrafficLogsService = useMemo(() => new ApiTrafficLogsService(), [])
  const [copiedAll, copyAll] = useCopyToClipboard()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [log, setLog] = useState(null)

  const fetchLog = useCallback(async () => {
    if (!id) return

    try {
      setLoading(true)
      setError(null)
      const res = await apiTrafficLogsService.getById(id)
      setLog(res.data)
    } catch (err) {
      const caught = CatchError(err, navigate, refreshToken)
      if (caught !== undefined) setError('Failed to load API traffic log entry.')
    } finally {
      setLoading(false)
    }
  }, [id, navigate, apiTrafficLogsService, refreshToken])

  useEffect(() => {
    fetchLog()
  }, [fetchLog])

  const buildFullRaw = () => {
    if (!log) return ''
    return JSON.stringify(log, null, 2)
  }

  if (loading) {
    return (
      <div className="page-content">
        <div className="d-flex align-items-center gap-2 py-5 justify-content-center">
          <i className="fa fa-circle-o-notch fa-spin" />
          <span>Loading API traffic log...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="page-content">
        <a href="/dashboard/api-traffic" className="btn btn-light text-black mb-3">
          <i className="fa-solid fa-arrow-left" /> Back
        </a>
        <div className="alert alert-danger">
          <i className="fa fa-exclamation-circle me-1" />
          {error}
        </div>
      </div>
    )
  }

  if (!log) return null

  return (
    <>
      <div className="page-heading">
        <div className="row align-items-center">
          <div className="col-12 col-md-6">
            <h3>API Traffic Details</h3>
            <p className="text-subtitle text-muted mb-0">
              Log ID: <code>{log.id}</code>
            </p>
          </div>
          <div className="col-12 col-md-6 text-md-end mt-2 mt-md-0">
            <button
              type="button"
              className={`btn btn-outline-secondary me-2${copiedAll ? ' copied' : ''}`}
              onClick={() => copyAll(buildFullRaw())}
            >
              <i className={`fa fa-${copiedAll ? 'check' : 'clipboard'}`} /> {copiedAll ? 'Copied' : 'Copy raw log'}
            </button>
            <a href="/dashboard/api-traffic" className="btn btn-light text-black">
              <i className="fa-solid fa-arrow-left" /> Back
            </a>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="row justify-content-center">
          <div className="col-12">
            <ApiTrafficLogSummary log={log} />

            <div className="row">
              <div className="col-12 col-lg-6">
                <JsonViewerPanel data={log.requestBody} title="Request Payload" />
              </div>
              <div className="col-12 col-lg-6">
                <JsonViewerPanel data={log.responseBody} title="Response Payload" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default ApiTrafficLogsDetail
